import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { linksApi } from "./api";
import type { CreateLinkRequest, ListLinksParams, UpdateLinkRequest } from "./types";

export const linkKeys = {
  all: ["links"] as const,
  lists: () => [...linkKeys.all, "list"] as const,
  list: (params: ListLinksParams) => [...linkKeys.lists(), params] as const,
  details: () => [...linkKeys.all, "detail"] as const,
  detail: (shortCode: string) => [...linkKeys.details(), shortCode] as const,
};

export function linksListOptions(params: ListLinksParams = {}) {
  return queryOptions({
    queryKey: linkKeys.list(params),
    queryFn: () => linksApi.list(params),
  });
}

export function linkDetailOptions(shortCode: string) {
  return queryOptions({
    queryKey: linkKeys.detail(shortCode),
    queryFn: () => linksApi.get(shortCode),
    enabled: !!shortCode,
  });
}

export function useCreateLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLinkRequest) => linksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: linkKeys.lists() });
    },
  });
}

export function useUpdateLink(shortCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateLinkRequest) => linksApi.update(shortCode, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: linkKeys.detail(shortCode) });
      queryClient.invalidateQueries({ queryKey: linkKeys.lists() });
    },
  });
}

export function useDeleteLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shortCode: string) => linksApi.delete(shortCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: linkKeys.lists() });
    },
  });
}
